import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const HaloNav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { language, toggleLanguage } = useLanguage();
    const t = translations[language];

    const searchableItems = [
        { label: t.nav_home, path: '/', type: t.nav_type_page, description: t.nav_desc_home },
        { label: t.nav_labs, path: '/labs', type: t.nav_type_page, description: t.nav_desc_labs },
        { label: t.nav_leadership, path: '/leadership', type: t.nav_type_page, description: t.nav_desc_leadership },
        { label: t.nav_projects, path: '/#projects', type: t.nav_type_section, description: t.nav_desc_projects },
        { label: t.nav_about, path: '/#about', type: t.nav_type_section, description: t.nav_desc_about },
        { label: t.nav_contact, path: '/#contact', type: t.nav_type_section, description: t.nav_desc_contact },
        { label: t.nav_services, path: '/#projects', type: t.nav_type_section, description: t.nav_desc_services },
        { label: 'SYSTEM DESIGN', path: '/#projects', type: t.nav_type_service, description: t.nav_desc_system },
        { label: 'INTERFACE DESIGN', path: '/#projects', type: t.nav_type_service, description: t.nav_desc_interface },
        { label: 'DIGITAL STRATEGY', path: '/#projects', type: t.nav_type_service, description: t.nav_desc_strategy },
    ];
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close overlays on route change
    useEffect(() => {
        setMenuOpen(false);
        setSearchOpen(false);
        setSearchQuery('');
    }, [location]);

    // Prevent scroll when overlays are open
    useEffect(() => {
        if (menuOpen || searchOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen, searchOpen]);

    // Focus search input when opening
    useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 300);
        }
    }, [searchOpen]);

    // Keyboard shortcut: Cmd+K or Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen(prev => !prev);
                setMenuOpen(false);
            }
            if (e.key === 'Escape') {
                setSearchOpen(false);
                setMenuOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const navLinks = [
        { label: t.nav_home, path: '/', number: '01' },
        { label: 'BASE', path: '/base', number: '02', isNew: true },
        { label: t.nav_labs, path: '/labs', number: '03' },
        { label: t.nav_leadership, path: '/leadership', number: '04' },
        { label: t.nav_projects, path: '/#projects', number: '05', isAnchor: true },
        { label: t.nav_about, path: '/#about', number: '06', isAnchor: true },
        { label: t.nav_contact, path: '/#contact', number: '07', isAnchor: true },
    ];

    const filteredResults = searchQuery.length > 0
        ? searchableItems.filter(item =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : searchableItems;

    const handleNavClick = (link) => {
        if (link.isAnchor) {
            setMenuOpen(false);
            if (location.pathname !== '/') {
                window.location.href = link.path;
            } else {
                const id = link.path.replace('/#', '');
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            setMenuOpen(false);
        }
    };

    const handleSearchSelect = (item) => {
        setSearchOpen(false);
        setSearchQuery('');
        if (item.path.startsWith('/#')) {
            if (location.pathname !== '/') {
                window.location.href = item.path;
            } else {
                const id = item.path.replace('/#', '');
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate(item.path);
        }
    };

    return (
        <>
            <motion.nav 
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                className={`fixed top-0 left-0 w-full z-[60] transition-all duration-500 border-b ${
                    scrolled ? 'bg-transparent backdrop-blur-md border-white/5 py-3 shadow-[0_4px_30px_rgba(0,0,0,0.5)]' : 'bg-transparent border-transparent py-6'
                }`}
            >
                <div className="w-full max-w-[1920px] mx-auto px-6 md:px-12 grid-sys">
                    
                    {/* Logo Area */}
                    <div className="col-span-6 flex items-center">
                        <Link 
                            to="/"
                            className="font-heading font-black text-2xl md:text-3xl tracking-tighter cursor-pointer hover:text-accent hover:drop-shadow-[0_0_15px_rgba(56,189,248,0.5)] transition-all duration-300"
                        >
                            MORATADEO
                        </Link>
                    </div>

                    {/* Right Side Tools */}
                    <div className="col-span-6 flex items-center justify-end gap-6">

                        {/* Conditional Base App links */}
                        {location.pathname.startsWith('/base') && (
                            <>
                                <Link 
                                    to="/base/privacy"
                                    className="hidden md:flex items-center justify-center border border-white/10 rounded-full px-4 py-2 text-[10px] font-mono uppercase text-white/70 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-accent/50 hover:text-accent hover:shadow-[0_0_20px_rgba(56,189,248,0.15)] transition-all duration-300 cursor-pointer"
                                >
                                    PRIVACY
                                </Link>
                                <Link 
                                    to="/base/tos"
                                    className="hidden md:flex items-center justify-center border border-white/10 rounded-full px-4 py-2 text-[10px] font-mono uppercase text-white/70 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-accent/50 hover:text-accent hover:shadow-[0_0_20px_rgba(56,189,248,0.15)] transition-all duration-300 cursor-pointer"
                                >
                                    TOS
                                </Link>
                                <a 
                                    href={location.pathname === '/base' ? '#get-base' : '/base#get-base'}
                                    onClick={(e) => {
                                        if (location.pathname === '/base') {
                                            const el = document.getElementById('get-base');
                                            if (el) {
                                                e.preventDefault();
                                                el.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                    className="hidden md:flex items-center gap-2 border border-accent rounded-full px-5 py-2 text-[10px] font-mono uppercase text-background bg-accent shadow-[0_0_15px_rgba(56,189,248,0.3)] hover:shadow-[0_0_25px_rgba(56,189,248,0.6)] hover:bg-white hover:text-accent hover:border-white transition-all duration-300 cursor-pointer font-bold tracking-widest"
                                >
                                    GET BASE &rarr;
                                </a>
                            </>
                        )}

                        {/* Search Pill - Now Glass Clickable */}
                        <button 
                            onClick={() => { setSearchOpen(true); setMenuOpen(false); }}
                            className="hidden md:flex items-center gap-2 border border-white/10 rounded-full px-5 py-2 text-[10px] font-mono uppercase text-white/70 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-accent/50 hover:text-accent hover:shadow-[0_0_20px_rgba(56,189,248,0.15)] transition-all duration-300 cursor-pointer"
                        >
                            <span>{t.nav_search}</span>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </button>

                        {/* Language Toggle */}
                        <button 
                            onClick={toggleLanguage}
                            className="hidden md:flex items-center justify-center border border-white/10 rounded-full px-4 py-2 text-[10px] font-mono uppercase text-white/70 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-accent/50 hover:text-accent hover:shadow-[0_0_20px_rgba(56,189,248,0.15)] transition-all duration-300 cursor-pointer"
                        >
                            {language === 'en' ? 'ES' : 'EN'}
                        </button>

                        {/* Hamburger Menu Button */}
                        <button 
                            onClick={() => { setMenuOpen(!menuOpen); setSearchOpen(false); }}
                            className="flex flex-col gap-[5px] justify-center items-end group w-8 h-8 cursor-pointer relative z-[70]"
                        >
                            <motion.div 
                                animate={menuOpen ? { rotate: 45, y: 6.5, width: 24 } : { rotate: 0, y: 0, width: 24 }}
                                className="h-[1.5px] bg-foreground group-hover:bg-accent transition-colors origin-center"
                            />
                            <motion.div 
                                animate={menuOpen ? { opacity: 0, x: 10 } : { opacity: 1, x: 0 }}
                                className="w-6 h-[1.5px] bg-foreground transition-all duration-300"
                            />
                            <motion.div 
                                animate={menuOpen ? { rotate: -45, y: -6.5, width: 24 } : { rotate: 0, y: 0, width: 24 }}
                                className="h-[1.5px] bg-foreground group-hover:bg-accent transition-colors origin-center"
                            />
                        </button>
                        
                    </div>
                </div>
            </motion.nav>

            {/* Search Overlay */}
            <AnimatePresence>
                {searchOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[55] bg-background/98 backdrop-blur-xl flex flex-col items-center pt-32 px-6"
                        onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
                    >
                        {/* Search Input */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: 0.1, duration: 0.3 }}
                            className="w-full max-w-2xl"
                        >
                            <div className="flex items-center gap-4 border-b border-border pb-4">
                                <svg className="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t.nav_search_placeholder}
                                    className="bg-transparent w-full text-2xl md:text-3xl font-heading tracking-tight text-foreground placeholder-muted/40 focus:outline-none"
                                />
                                <button onClick={() => setSearchOpen(false)} className="font-mono text-[10px] text-muted border border-border rounded px-2 py-1 hover:text-foreground hover:border-foreground transition-colors">
                                    {t.nav_esc}
                                </button>
                            </div>

                            {/* Results */}
                            <div className="mt-6 flex flex-col">
                                <span className="font-mono text-[10px] text-muted uppercase tracking-widest mb-3">
                                    {searchQuery ? `${filteredResults.length} ${t.nav_results}` : t.nav_quick_nav}
                                </span>
                                {filteredResults.map((item, i) => (
                                    <motion.button
                                        key={item.label}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.03 * i }}
                                        onClick={() => handleSearchSelect(item)}
                                        className="flex items-center justify-between py-4 border-b border-border/50 hover:bg-white/[0.02] px-3 -mx-3 transition-colors text-left group cursor-pointer"
                                    >
                                        <div className="flex flex-col gap-1">
                                            <span className="text-lg font-heading tracking-tight text-foreground group-hover:text-accent transition-colors">
                                                {item.label}
                                            </span>
                                            <span className="font-mono text-[10px] text-muted uppercase tracking-widest">
                                                {item.description}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-[9px] text-muted uppercase tracking-widest border border-border rounded-full px-2 py-0.5">
                                                {item.type}
                                            </span>
                                            <svg className="w-4 h-4 text-muted group-hover:text-accent transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                        </div>
                                    </motion.button>
                                ))}
                                {filteredResults.length === 0 && (
                                    <div className="py-12 text-center">
                                        <span className="font-mono text-sm text-muted uppercase">{t.nav_no_results}</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Fullscreen Menu Overlay */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[55] bg-background/98 backdrop-blur-xl flex items-center justify-center"
                    >
                        <div className="w-full max-w-[1920px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-12">
                            
                            {/* Navigation Links */}
                            <nav className="flex flex-col gap-2">
                                {navLinks.map((link, i) => (
                                    <motion.div
                                        key={link.label}
                                        initial={{ opacity: 0, x: -40 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -40 }}
                                        transition={{ delay: 0.05 * i, duration: 0.4 }}
                                    >
                                        {link.isAnchor ? (
                                            <button
                                                onClick={() => handleNavClick(link)}
                                                className={`flex items-baseline gap-6 group cursor-pointer text-left ${
                                                    location.hash === link.path.replace('/', '') ? 'text-accent' : 'text-foreground'
                                                }`}
                                            >
                                                <span className="font-mono text-xs text-muted group-hover:text-accent transition-colors w-8">
                                                    {link.number}
                                                </span>
                                                <span className="text-5xl md:text-7xl font-heading font-extrabold tracking-tighter group-hover:text-accent transition-colors leading-tight flex items-center gap-4">
                                                    {link.label}
                                                    {link.isNew && (
                                                        <span className="text-[12px] md:text-sm font-mono bg-accent text-background px-2 py-1 rounded-full uppercase tracking-widest translate-y-[-10px] md:translate-y-[-20px] shadow-[0_0_15px_rgba(56,189,248,0.5)]">
                                                            NEW
                                                        </span>
                                                    )}
                                                </span>
                                            </button>
                                        ) : (
                                            <Link
                                                to={link.path}
                                                onClick={() => handleNavClick(link)}
                                                className={`flex items-baseline gap-6 group ${
                                                    location.pathname === link.path ? 'text-accent' : 'text-foreground'
                                                }`}
                                            >
                                                <span className="font-mono text-xs text-muted group-hover:text-accent transition-colors w-8">
                                                    {link.number}
                                                </span>
                                                <span className="text-5xl md:text-7xl font-heading font-extrabold tracking-tighter group-hover:text-accent transition-colors leading-tight flex items-center gap-4">
                                                    {link.label}
                                                    {link.isNew && (
                                                        <span className="text-[12px] md:text-sm font-mono bg-accent text-background px-2 py-1 rounded-full uppercase tracking-widest translate-y-[-10px] md:translate-y-[-20px] shadow-[0_0_15px_rgba(56,189,248,0.5)]">
                                                            NEW
                                                        </span>
                                                    )}
                                                </span>
                                            </Link>
                                        )}
                                    </motion.div>
                                ))}
                            </nav>

                            {/* Right Side Info */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ delay: 0.3, duration: 0.5 }}
                                className="flex flex-col gap-8"
                            >
                                <div>
                                    <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{t.nav_contact}</span>
                                    <button 
                                        onClick={() => { setMenuOpen(false); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300); }}
                                        className="block font-mono text-sm text-foreground hover:text-accent transition-colors mt-2 cursor-pointer"
                                    >
                                        {t.nav_get_in_touch}
                                    </button>
                                </div>
                                <div>
                                    <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{t.nav_location}</span>
                                    <p className="font-mono text-sm text-foreground mt-2">LOS ANGELES, CA</p>
                                </div>
                                <div>
                                    <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{t.nav_socials}</span>
                                    <div className="flex gap-4 mt-2">
                                        <button className="font-mono text-xs text-muted hover:text-accent transition-colors cursor-pointer">INSTAGRAM</button>
                                        <button className="font-mono text-xs text-muted hover:text-accent transition-colors cursor-pointer">LINKEDIN</button>
                                        <button className="font-mono text-xs text-muted hover:text-accent transition-colors cursor-pointer">GITHUB</button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default HaloNav;
